export const LOG_CAP = 90;

export const SYMPTOM_PRESETS = [
  'Pain',
  'Nausea',
  'Vomiting',
  'Fatigue',
  'Appetite',
  'Sleep',
  'Mood',
  'Anxiety',
  'Neuropathy',
  'Fever',
  'Mouth sores',
  'Diarrhea',
  'Constipation',
  'Shortness of breath',
  'Skin changes',
  'Swelling',
  'Cough',
  'Other',
];

export const IMPACT_OPTIONS = [
  { value: 0, label: 'Not at all' },
  { value: 1, label: 'A little' },
  { value: 2, label: 'Somewhat' },
  { value: 3, label: 'Quite a bit' },
  { value: 4, label: 'Very much' },
];

export const QUICK_SEVERITIES = [0, 2, 4, 6, 8, 10];

export function splitPrompts(text) {
  return String(text || '')
    .split(/\n+/)
    .map((line) => line.replace(/^[-*•]\s*/, '').trim())
    .filter(Boolean);
}

export function parseSchedule(text) {
  const t = String(text || '').toLowerCase();
  let cadence = 'When you want';
  if (/sunday|weekly|each week|every week/.test(t)) cadence = 'Weekly';
  else if (/every morning|each morning|mornings/.test(t)) cadence = 'Mornings';
  else if (/every evening|each evening|nightly|every night/.test(t)) cadence = 'Evenings';
  else if (/daily|every day|each day/.test(t)) cadence = 'Daily';

  const clock = String(text || '').match(/\b(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)\b/i);
  let time_label = '';
  let local_hour = null;
  let local_minute = null;
  if (clock) {
    let hour = Number(clock[1]);
    const minute = clock[2] ? Number(clock[2]) : 0;
    const ap = /p/i.test(clock[3] || '') ? 'pm' : (/a/i.test(clock[3] || '') ? 'am' : '');
    if (ap === 'pm' && hour < 12) hour += 12;
    if (ap === 'am' && hour === 12) hour = 0;
    const min = clock[2] ? `:${clock[2]}` : '';
    time_label = ap ? `${Number(clock[1])}${min} ${ap}` : `${hour}${min}`;
    local_hour = hour;
    local_minute = minute;
  } else if (/\bnoon\b/i.test(text)) {
    time_label = 'noon';
    local_hour = 12;
    local_minute = 0;
  }

  if (local_hour == null) {
    if (cadence === 'Mornings') local_hour = 8;
    else if (cadence === 'Evenings') local_hour = 20;
    else if (cadence === 'Weekly') local_hour = 10;
    else if (cadence === 'Daily') local_hour = 9;
  }
  if (local_hour != null && local_minute == null) local_minute = 0;

  return { cadence, time_label, local_hour, local_minute };
}

export function loggedToday(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return d.toDateString() === new Date().toDateString();
}

export function lastLoggedLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  if (loggedToday(iso)) return 'Logged today';
  return `Last logged ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
}

export function scheduleLabel(item) {
  const cadence = item.cadence && item.cadence !== 'custom' ? item.cadence : '';
  const time = item.time_label || '';
  const when = [cadence, time].filter(Boolean).join(' · ');
  return when || 'When you want';
}

export function clampSeverity(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(10, Math.round(n)));
}

export function clampImpact(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(4, Math.round(n)));
}

export function severityLabel(severity) {
  const n = clampSeverity(severity);
  if (n <= 0) return 'None';
  if (n <= 2) return 'Mild';
  if (n <= 4) return 'Low';
  if (n <= 6) return 'Moderate';
  if (n <= 8) return 'High';
  return 'Severe';
}

export function impactLabel(impact) {
  const n = clampImpact(impact);
  if (n == null) return '';
  return IMPACT_OPTIONS.find((row) => row.value === n)?.label || '';
}

export function normalizeLog(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const recorded_at = entry.recorded_at || entry.at || new Date().toISOString();
  const impact = clampImpact(entry.impact);
  const out = {
    severity: clampSeverity(entry.severity),
    note: String(entry.note || '').trim().slice(0, 800),
    recorded_at,
  };
  if (impact != null) out.impact = impact;
  return out;
}

/** Append a log locally; caps at LOG_CAP newest-last entries. */
export function appendLog(item, entry) {
  const log = normalizeLog(entry);
  if (!log) return item;
  const logs = [...(item?.logs || []), log].slice(-LOG_CAP);
  return {
    ...item,
    logs,
    last_completed_at: log.recorded_at,
  };
}

export function recentLogs(item, limit = 14) {
  const logs = Array.isArray(item?.logs) ? item.logs : [];
  return logs.slice(-Math.max(1, limit));
}

/**
 * Simple trend from the last 7-14 logs: rising / easing / steady / none.
 */
export function trendSummary(item, lookback = 14) {
  const logs = recentLogs(item, lookback);
  if (logs.length < 2) {
    return {
      label: logs.length ? 'Getting started' : 'No logs yet',
      direction: 'none',
      avg: logs[0] ? logs[0].severity : null,
      points: logs.map((row) => row.severity),
    };
  }
  const half = Math.max(1, Math.floor(logs.length / 2));
  const first = logs.slice(0, half);
  const second = logs.slice(-half);
  const avg = (rows) => rows.reduce((s, r) => s + r.severity, 0) / rows.length;
  const a = avg(first);
  const b = avg(second);
  const delta = b - a;
  let direction = 'steady';
  let label = 'Holding steady';
  if (delta >= 1) {
    direction = 'rising';
    label = 'Trending higher';
  } else if (delta <= -1) {
    direction = 'easing';
    label = 'Easing down';
  }
  return {
    label,
    direction,
    avg: Math.round(b * 10) / 10,
    points: logs.map((row) => row.severity),
  };
}

export function isSymptomKind(item) {
  const kind = String(item?.kind || 'symptom').toLowerCase();
  return kind !== 'reminder';
}

function dayKey(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toDateString();
}

/** Consecutive local calendar days ending today with any symptom log. */
export function adherenceStreak(items) {
  const days = new Set();
  (items || []).filter(isSymptomKind).forEach((item) => {
    (item.logs || []).forEach((entry) => {
      const key = dayKey(entry.recorded_at);
      if (key) days.add(key);
    });
  });
  let streak = 0;
  const cursor = new Date();
  cursor.setHours(12, 0, 0, 0);
  while (days.has(cursor.toDateString())) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

/**
 * Overview strip for the Symptoms page.
 */
export function overviewStats(items) {
  const symptoms = (items || []).filter(isSymptomKind);
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  let logsThisWeek = 0;
  const days = new Set();
  const severities = [];
  const needsAttention = [];

  symptoms.forEach((item) => {
    (item.logs || []).forEach((entry) => {
      const t = new Date(entry.recorded_at).getTime();
      if (Number.isNaN(t) || t < weekAgo) return;
      logsThisWeek += 1;
      days.add(dayKey(entry.recorded_at));
      severities.push(entry.severity);
      if (entry.severity >= 7) {
        needsAttention.push({
          prompt: item.prompt,
          severity: entry.severity,
          recorded_at: entry.recorded_at,
        });
      }
    });
  });

  const rising = symptoms
    .filter((item) => trendSummary(item).direction === 'rising')
    .map((item) => item.prompt);

  return {
    trackers: symptoms.length,
    logsThisWeek,
    daysLoggedThisWeek: days.size,
    streakDays: adherenceStreak(items),
    avgSeverityThisWeek: severities.length
      ? Math.round((severities.reduce((s, n) => s + n, 0) / severities.length) * 10) / 10
      : null,
    needsAttention: needsAttention.slice(-5),
    rising,
  };
}

export function clinicBriefText(items, lookbackDays = 14) {
  const since = Date.now() - lookbackDays * 24 * 60 * 60 * 1000;
  const lines = [
    'Symptom log for my oncology visit',
    `Period: last ${lookbackDays} days (self-reported for clinic conversation).`,
    '',
  ];
  let any = false;
  (items || []).filter(isSymptomKind).forEach((item) => {
    const entries = (item.logs || []).filter((entry) => {
      const t = new Date(entry.recorded_at).getTime();
      return !Number.isNaN(t) && t >= since;
    });
    if (!entries.length) return;
    any = true;
    const sevs = entries.map((e) => e.severity);
    const avg = Math.round((sevs.reduce((s, n) => s + n, 0) / sevs.length) * 10) / 10;
    const peak = Math.max(...sevs);
    const trend = trendSummary({ ...item, logs: entries });
    const trendWord = {
      rising: 'trending higher',
      easing: 'easing',
      steady: 'steady',
      none: 'limited data',
    }[trend.direction] || 'steady';
    lines.push(
      `${item.prompt}: ${entries.length} log(s), avg ${avg}/10, peak ${peak}/10, ${trendWord}.`
    );
    entries.slice(-5).forEach((entry) => {
      const when = new Date(entry.recorded_at).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
      const impact = impactLabel(entry.impact);
      const impactBit = impact ? `, interference ${impact}` : '';
      const noteBit = entry.note ? `, ${entry.note}` : '';
      lines.push(`  · ${when}: ${entry.severity}/10${impactBit}${noteBit}`);
    });
    lines.push('');
  });
  if (!any) lines.push('No severity logs in this period yet.');
  else lines.push('Please review these trends and logs with your care team.');
  return `${lines.join('\n').trim()}\n`;
}
